import { LinearProgress } from "@mui/material";
import {
	UnsetSecondaryCopy,
	UploadCSVFileInfoProgressBar,
	UploadErrorWaringText,
	UploadModalCSVFileAttachmentViewBytes,
	UploadModalCSVFileInfoContainer,
	UploadModalCSVFileStatusText
} from "../NewOrders.styles";
import { formatBytes } from "utils/file";
import Close from "@mui/icons-material/Close";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import {
	NewOrderGenericUploadError,
	NewOrderImportSelectionDoesntMatch,
	NewOrderNoConfigsError,
	NewOrderUnsupportedFile
} from "../constants";
import { FileSuccessText, HereText, NoAccountText, ProperlyExportText } from "constants/text";
import Link from "@mui/material/Link";
import { ErrorCodeData } from "../CSVUpload/CSVUpload";

interface CSVFileUploadProgressProps {
	file: File;
	isUpdating?: boolean;
	hasError?: boolean;
	isAttemptedValidation?: boolean;
	errors?: any;
	isSuccess?: boolean;
	errorCodes?: ErrorCodeData;
	removeFile: () => void;
	isAddOn?: boolean;
}

const renderUploadStatus = (message: string, condition?: boolean) =>
	condition && <UploadErrorWaringText>{message}</UploadErrorWaringText>;

const CSVFileUploadProgress = ({
	file,
	isUpdating,
	hasError,
	isAttemptedValidation,
	errors,
	isSuccess,
	errorCodes,
	removeFile,
	isAddOn
}: CSVFileUploadProgressProps) => {
	const exportCSVPlaylistURL =
		"https://tandem.cabinetworksgroup.com/apex/scormanywhere__SCORM_Player?JoUqcFNReiddDQTAXdRv9bAe8FEe%2f%2Br9lV7SIRK7nccFIghAk3tx2BJGyM1uNqnQ87hUSq64wRp3B7C5fLoEWQ%3D%3D";
	const uploadGenericError =
		!errorCodes?.invalidCSVData &&
		!errorCodes?.invalidCSVFile &&
		!errorCodes?.invalidCSVType &&
		!errorCodes?.unauthorizedProductLine &&
		hasError;

	return (
		<UploadModalCSVFileInfoContainer
			key={file.name}
			isError={hasError}
			isSuccess={isSuccess}
		>
			<p data-testid="new-order-csv-upload-selected-file-name">
				{file.name}
				<UploadModalCSVFileAttachmentViewBytes
					data-testid="new-order-csv-upload-selected-file-size"
					isError={hasError}
				>
					{formatBytes(file.size)}
				</UploadModalCSVFileAttachmentViewBytes>
			</p>
			{(isAttemptedValidation || isUpdating) && (
				<UploadCSVFileInfoProgressBar data-testid="new-order-csv-upload-progress-bar">
					<LinearProgress
						color={hasError ? "error" : "primary"}
						variant={isUpdating && !hasError && !isSuccess ? "indeterminate" : "determinate"}
						value={hasError ? 2 : 100}
					/>
					{!isUpdating && (
						<Close
							onClick={removeFile}
							data-testid="new-order-csv-upload-remove-file"
						/>
					)}
				</UploadCSVFileInfoProgressBar>
			)}
			{(isSuccess || hasError) && (
				<UploadModalCSVFileStatusText
					isSuccess={isSuccess}
					data-testid="new-order-csv-upload-status-text"
				>
					{isSuccess ? (
						<CheckCircleOutline data-testid="new-order-csv-upload-status-success-svg" />
					) : (
						<ErrorOutline data-testid="new-order-csv-upload-status-error-svg" />
					)}
					{errorCodes?.invalidCSVData && (
						<UploadErrorWaringText data-testid="new-order-csv-upload-error-unsupported-file-text">
							{NewOrderImportSelectionDoesntMatch}
							{errorCodes?.invalidCSVData && (
								<ul>{errors?.map((error: any) => <li key={error.message}>{error.message}</li>)}</ul>
							)}
						</UploadErrorWaringText>
					)}
					{renderUploadStatus(NewOrderNoConfigsError, errorCodes?.invalidCSVFile)}
					{renderUploadStatus(NewOrderUnsupportedFile, errorCodes?.invalidCSVType)}
					{renderUploadStatus(NoAccountText, errorCodes?.unauthorizedProductLine)}
					{renderUploadStatus(NewOrderGenericUploadError, uploadGenericError)}
					{renderUploadStatus(FileSuccessText, isSuccess)}
				</UploadModalCSVFileStatusText>
			)}
			{errorCodes?.invalidCSVType && (
				<UnsetSecondaryCopy isError={errorCodes?.invalidCSVType}>
					{ProperlyExportText}
					<Link
						href={exportCSVPlaylistURL}
						target="_blank"
						data-testid="newOrder-importFile-properlyExportLink"
						data-id={`${isAddOn ? "add-ons" : "new-order"}-properly-export-csv-link`}
					>
						{HereText}
					</Link>
				</UnsetSecondaryCopy>
			)}
		</UploadModalCSVFileInfoContainer>
	);
};

export default CSVFileUploadProgress;
